// Day-planner field registry, the analogue of lib/session-planner/sport-config.ts
// for the resort (ski / MTB) planner. It declares, per AreaSport, the ordered
// list of preference controls the PreferencesPanel renders. Keeping the field
// set in data (rather than hard-coded JSX) lets one panel drive both sports and
// keeps the option vocabularies in one place. Phase 6 wires PreferencesPanel to
// read from here; this module only declares the config.

import type { AreaSport } from "./graph-types";

export type PrefOption = { value: string; label: string };

export type PrefField = {
  key: string;
  label: string;
  options: PrefOption[];
  default: string;
  /** Conditional visibility based on the other selected values. */
  showIf?: (values: Record<string, string>) => boolean;
};

// ---------------------------------------------------------------------------
// Shared option sets (reused across both sports per Decision 7)
// ---------------------------------------------------------------------------

const ABILITY: PrefOption[] = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
];

const DIFFICULTY_MODE: PrefOption[] = [
  { value: "comfort", label: "Comfort zone" },
  { value: "push", label: "Push a little" },
  { value: "challenge", label: "Challenge me" },
];

const DISTANCE: PrefOption[] = [
  { value: "short", label: "Short" },
  { value: "half-day", label: "Half day" },
  { value: "full-day", label: "Full day" },
  { value: "max", label: "Max distance" },
];

const GROUP: PrefOption[] = [
  { value: "solo", label: "Solo" },
  { value: "couple", label: "Couple" },
  { value: "family", label: "Family" },
  { value: "mates", label: "Mates" },
];

const LUNCH_STYLE: PrefOption[] = [
  { value: "skip", label: "Skip" },
  { value: "packed", label: "Backpack" },
  { value: "cheap", label: "Mountain cafe" },
  { value: "sit-down", label: "Sit-down" },
];

const COFFEE_STOPS: PrefOption[] = [
  { value: "skip", label: "Skip" },
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "both", label: "Both" },
];

const APRES: PrefOption[] = [
  { value: "skip", label: "Skip" },
  { value: "coffee-cake", label: "Coffee and cake" },
  { value: "quiet", label: "Quiet drink" },
  { value: "party", label: "Big party" },
];

const RETURN_TO_START: PrefOption[] = [
  { value: "true", label: "End where I started" },
  { value: "false", label: "Finish at any base" },
];

// ---------------------------------------------------------------------------
// Ski-only option sets
// ---------------------------------------------------------------------------

const POLING: PrefOption[] = [
  { value: "fine", label: "Do not mind poling" },
  { value: "minimise", label: "Keep it rolling" },
];

const TREE_RUNS: PrefOption[] = [
  { value: "prefer", label: "Prefer" },
  { value: "no-preference", label: "No preference" },
  { value: "avoid", label: "Avoid" },
];

// ---------------------------------------------------------------------------
// MTB-only option sets
// ---------------------------------------------------------------------------

const RIDING_STYLE: PrefOption[] = [
  { value: "flow", label: "Flow" },
  { value: "technical-enduro", label: "Technical / enduro" },
  { value: "downhill-park", label: "Downhill / bike park" },
  { value: "cross-country", label: "Cross-country" },
  { value: "e-bike-friendly", label: "E-bike friendly" },
];

const FITNESS: PrefOption[] = [
  { value: "low", label: "Taking it easy" },
  { value: "moderate", label: "Moderate" },
  { value: "high", label: "Fit" },
  { value: "very-high", label: "Very fit" },
];

const CLIMBING_TOLERANCE: PrefOption[] = [
  { value: "lift-only", label: "Lift only" },
  { value: "some-pedalling", label: "Some pedalling" },
  { value: "happy-to-climb", label: "Happy to climb" },
  { value: "maximise", label: "Maximise climbing" },
];

const BIKE_TYPE: PrefOption[] = [
  { value: "hardtail", label: "Hardtail" },
  { value: "full-suspension", label: "Full suspension" },
  { value: "e-bike", label: "E-bike" },
  { value: "dh", label: "Downhill" },
];

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

const SKI_FIELDS: PrefField[] = [
  { key: "ability", label: "Ability", options: ABILITY, default: "intermediate" },
  { key: "difficultyMode", label: "Push yourself?", options: DIFFICULTY_MODE, default: "comfort" },
  { key: "distance", label: "How far?", options: DISTANCE, default: "full-day" },
  { key: "group", label: "Group", options: GROUP, default: "couple" },
  { key: "poling", label: "Flat sections", options: POLING, default: "fine" },
  { key: "treeRuns", label: "Tree-lined runs", options: TREE_RUNS, default: "no-preference" },
  { key: "lunch", label: "Lunch", options: LUNCH_STYLE, default: "cheap" },
  { key: "coffeeStops", label: "Coffee stops", options: COFFEE_STOPS, default: "skip" },
  { key: "apres", label: "Apres", options: APRES, default: "skip" },
  { key: "returnToStart", label: "Finish", options: RETURN_TO_START, default: "true" },
];

const MTB_FIELDS: PrefField[] = [
  { key: "ability", label: "Ability", options: ABILITY, default: "intermediate" },
  { key: "difficultyMode", label: "Push yourself?", options: DIFFICULTY_MODE, default: "comfort" },
  { key: "ridingStyle", label: "Riding style", options: RIDING_STYLE, default: "flow" },
  { key: "fitness", label: "Fitness", options: FITNESS, default: "moderate" },
  { key: "climbingTolerance", label: "Climbing", options: CLIMBING_TOLERANCE, default: "some-pedalling" },
  { key: "distance", label: "How far?", options: DISTANCE, default: "full-day" },
  { key: "bikeType", label: "Bike", options: BIKE_TYPE, default: "full-suspension" },
  { key: "group", label: "Group", options: GROUP, default: "couple" },
  { key: "lunch", label: "Lunch", options: LUNCH_STYLE, default: "cheap" },
  { key: "coffeeStops", label: "Coffee stops", options: COFFEE_STOPS, default: "skip" },
  { key: "apres", label: "Apres", options: APRES, default: "skip" },
  { key: "returnToStart", label: "Finish", options: RETURN_TO_START, default: "true" },
];

export const PLANNER_SPORT_FIELDS: Record<AreaSport, PrefField[]> = {
  ski: SKI_FIELDS,
  mtb: MTB_FIELDS,
};
